import React, { useEffect, useState } from "react";
import style from "./SubmissionDetail.module.css";

// Canned rejection reasons the creator will see. Keep these human-readable —
// they are shown verbatim to the creator alongside the optional note.
export const REJECT_REASONS = [
  "Poor video quality / low resolution",
  "Audio issues",
  "Copyright / rights concern",
  "Inappropriate or prohibited content",
  "Incomplete or corrupted upload",
  "Metadata / details incorrect",
  "Duplicate submission",
  "Other",
];

/**
 * Reject dialog: reason dropdown + optional note, plus an opt-in
 * "email the creator" toggle (email sending is gated on the backend).
 *
 * `takedown` switches the copy for content that is ALREADY LIVE (a trusted
 * creator's upload publishes with no review, so this is the one-click way to
 * pull it). Same request either way — qc_status goes to Rejected, which stops
 * satisfying the public gate — but the wording has to say "this is live now",
 * not "this submission is refused".
 */
const RejectModal = ({
  open,
  title,
  submitting,
  onCancel,
  onConfirm,
  takedown = false,
  // Only passed for a takedown, and only when the owner is a trusted creator —
  // there is nothing to remove otherwise.
  creatorName = "",
  canRemoveTrust = false,
}) => {
  const [reason, setReason] = useState(REJECT_REASONS[0]);
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  // Default ON: content only gets pulled when something went wrong, which is
  // exactly when that creator should stop publishing unreviewed. Without this
  // they can re-edit the item and it republishes itself straight away.
  const [removeTrust, setRemoveTrust] = useState(true);

  useEffect(() => {
    if (open) {
      setReason(REJECT_REASONS[0]);
      setNote("");
      setNotify(true);
      setRemoveTrust(true);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className={style.modalOverlay} onClick={submitting ? undefined : onCancel}>
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={style.modalTitle}>
          {takedown ? "Take down this content" : "Reject submission"}
        </h3>
        <p className={style.modalSub}>
          {takedown ? (
            <>
              {title ? `“${title}”` : "This content"} is live now. Taking it down
              removes it from the platform immediately. The creator sees the
              reason and can fix it and resubmit.
            </>
          ) : (
            <>
              Rejecting {title ? `“${title}”` : "this submission"}. The creator
              will see the reason and can resubmit.
            </>
          )}
        </p>

        <label className={style.modalLabel}>Reason</label>
        <select
          className={style.modalSelect}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
        >
          {REJECT_REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        <textarea
          className={style.modalTextarea}
          placeholder="Add a note for the creator (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={submitting}
          rows={3}
        />

        {takedown && canRemoveTrust && (
          <label className={style.notifyRow}>
            <input
              type="checkbox"
              checked={removeTrust}
              onChange={(e) => setRemoveTrust(e.target.checked)}
              disabled={submitting}
            />
            Also require review on {creatorName || "this creator"}'s future
            uploads
          </label>
        )}

        <label className={style.notifyRow}>
          <input
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            disabled={submitting}
          />
          Email the creator about this decision
        </label>

        <div className={style.modalActions}>
          <button
            className={style.modalCancel}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className={style.modalReject}
            onClick={() =>
              onConfirm({
                reason,
                note: note.trim(),
                notify,
                removeTrust: takedown && canRemoveTrust ? removeTrust : false,
              })
            }
            disabled={submitting}
          >
            {submitting
              ? takedown
                ? "Taking down…"
                : "Rejecting…"
              : takedown
              ? "Take down"
              : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectModal;
